import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const ProfilePage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const { user, setUser } = useAuth();

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [validation, setValidation] = useState({
    passwordStrength: 0,
    passwordError: "",
    profileErrors: {},
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: user.role || "",
      });
    }
  }, [user]);

  // Validate password strength
  const validatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return Math.min(strength, 5);
  };

  const handlePasswordChange = (field, value) => {
    setPasswords((prev) => ({ ...prev, [field]: value }));

    if (field === "newPassword") {
      const strength = validatePassword(value);
      setValidation((prev) => ({
        ...prev,
        passwordStrength: strength,
      }));
    }
  };

  const handleProfileUpdate = async () => {
    const errors = {};
    if (!profile.name.trim()) errors.name = "Name is required";
    if (
      profile.phone &&
      !/^[0-9]{10}$/.test(profile.phone.replace(/\D/g, ""))
    ) {
      errors.phone = "Invalid phone number";
    }

    if (Object.keys(errors).length > 0) {
      setValidation((prev) => ({ ...prev, profileErrors: errors }));
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(
        `${import.meta.env.VITE_API_BASE_URL}/api/users/me`,
        {
          name: profile.name.trim(),
          phone: profile.phone.replace(/\D/g, ""),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setUser(res.data.user);
      setValidation((prev) => ({ ...prev, profileErrors: {} }));

      // Show success toast
      document.dispatchEvent(
        new CustomEvent("toast", {
          detail: {
            type: "success",
            message: "Profile updated successfully",
          },
        })
      );
    } catch (err) {
      document.dispatchEvent(
        new CustomEvent("toast", {
          detail: {
            type: "error",
            message: err.response?.data?.message || "Failed to update profile",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      setValidation((prev) => ({
        ...prev,
        passwordError: "New password and confirm password do not match",
      }));
      return;
    }

    if (validatePassword(passwords.newPassword) < 3) {
      setValidation((prev) => ({
        ...prev,
        passwordError:
          "Password must be at least 8 characters with uppercase, lowercase and numbers",
      }));
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/users/change-password1`,
        passwords,
        {
          withCredentials: true, 
        }
      );

      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setValidation((prev) => ({
        ...prev,
        passwordError: "",
        passwordStrength: 0,
      }));

      document.dispatchEvent(
        new CustomEvent("toast", {
          detail: {
            type: "success",
            message: "Password changed successfully",
          },
        })
      );
    } catch (err) {
      setValidation((prev) => ({
        ...prev,
        passwordError:
          err.response?.data?.message || "Failed to change password",
      }));
    } finally {
      setLoading(false);
    }
  };

  // Calculate password strength color
  const getPasswordStrengthColor = () => {
    const strength = validation.passwordStrength;
    if (strength <= 2) return "bg-red-500";
    if (strength <= 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar isOpen={sidebarOpen} />

      <main className="pt-16 lg:ml-60">
        <div className="p-4 md:p-6">
          <Breadcrumb />

          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Profile Settings
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage your account settings and preferences
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  {user?.role || "User"}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar - Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sticky top-24">
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg">
                    <span className="text-3xl font-bold text-white">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </span>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {user?.name || "User"}
                  </h2>
                  <p className="text-sm text-gray-600 mb-1">
                    {user?.email || "user@example.com"}
                  </p>
                  <p className="text-xs text-gray-500 mb-6">
                    {user?.phone || "No phone number"}
                  </p>

                  {/* Navigation Tabs */}
                  <div className="w-full space-y-1">
                    <button
                      onClick={() => setActiveTab("profile")}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === "profile"
                          ? "bg-blue-50 text-blue-700 border border-blue-100"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}>
                      <Icon name="User" size={16} />
                      <span className="text-sm font-medium">
                        Profile Information
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab("security")}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === "security"
                          ? "bg-blue-50 text-blue-700 border border-blue-100"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}>
                      <Icon name="Lock" size={16} />
                      <span className="text-sm font-medium">Security</span>
                    </button>
                    <button
                      onClick={() => setActiveTab("permissions")}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === "permissions"
                          ? "bg-blue-50 text-blue-700 border border-blue-100"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}>
                      <Icon name="ShieldCheck" size={16} />
                      <span className="text-sm font-medium">Access Rights</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="lg:col-span-2">
              {/* Profile Information Tab */}
              {activeTab === "profile" && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Profile Information
                        </h2>
                        <p className="text-sm text-gray-600">
                          Update your personal details
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={profile.name}
                              onChange={(e) =>
                                setProfile({ ...profile, name: e.target.value })
                              }
                              className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                validation.profileErrors.name
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                              placeholder="Enter your full name"
                            />
                            {validation.profileErrors.name && (
                              <p className="mt-1 text-xs text-red-600">
                                {validation.profileErrors.name}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              value={profile.email}
                              disabled
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500"
                            />
                            <div className="absolute right-3 top-3">
                              <Icon
                                name="Mail"
                                size={16}
                                className="text-gray-400"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <div className="relative">
                            <input
                              type="tel"
                              value={profile.phone}
                              onChange={(e) =>
                                setProfile({
                                  ...profile,
                                  phone: e.target.value,
                                })
                              }
                              className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                validation.profileErrors.phone
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                              placeholder="+1 (555) 123-4567"
                            />
                            {validation.profileErrors.phone && (
                              <p className="mt-1 text-xs text-red-600">
                                {validation.profileErrors.phone}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Role
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={profile.role}
                              disabled
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500"
                            />
                            <div className="absolute right-3 top-3">
                              <Icon
                                name="Briefcase"
                                size={16}
                                className="text-gray-400"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex justify-end">
                          <Button
                            onClick={handleProfileUpdate}
                            disabled={loading}
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2.5 text-sm font-medium rounded-lg shadow-sm">
                            {loading ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Icon name="Save" size={14} />
                                Save Changes
                              </div>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === "security" && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <Icon name="Lock" size={20} className="text-red-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Security Settings
                        </h2>
                        <p className="text-sm text-gray-600">
                          Change your password and secure your account
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="space-y-6">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Icon
                            name="AlertTriangle"
                            size={18}
                            className="text-yellow-600 mt-0.5"
                          />
                          <div>
                            <h3 className="text-sm font-medium text-yellow-800">
                              Security Recommendation
                            </h3>
                            <p className="text-xs text-yellow-700 mt-1">
                              Use a strong password with at least 8 characters
                              including uppercase, lowercase, numbers, and
                              symbols.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password *
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword.current ? "text" : "password"}
                              value={passwords.currentPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "currentPassword",
                                  e.target.value
                                )
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter your current password"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowPassword((prev) => ({
                                  ...prev,
                                  current: !prev.current,
                                }))
                              }
                              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700">
                              <Icon
                                name={showPassword.current ? "EyeOff" : "Eye"}
                                size={18}
                              />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password *
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword.new ? "text" : "password"}
                              value={passwords.newPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "newPassword",
                                  e.target.value
                                )
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter new password"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowPassword((prev) => ({
                                  ...prev,
                                  new: !prev.new,
                                }))
                              }
                              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700">
                              <Icon
                                name={showPassword.new ? "EyeOff" : "Eye"}
                                size={18}
                              />
                            </button>
                          </div>

                          {/* Password Strength Meter */}
                          {passwords.newPassword && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-gray-600">
                                  Password strength
                                </span>
                                <span className="text-xs font-medium text-gray-700">
                                  {validation.passwordStrength}/5
                                </span>
                              </div>
                              <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                                  style={{
                                    width: `${
                                      (validation.passwordStrength / 5) * 100
                                    }%`,
                                  }}></div>
                              </div>
                              <div className="mt-2 grid grid-cols-5 gap-1">
                                {[1, 2, 3, 4, 5].map((level) => (
                                  <div
                                    key={level}
                                    className={`h-1 rounded-full ${
                                      level <= validation.passwordStrength
                                        ? getPasswordStrengthColor()
                                        : "bg-gray-200"
                                    }`}></div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password *
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword.confirm ? "text" : "password"}
                              value={passwords.confirmPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "confirmPassword",
                                  e.target.value
                                )
                              }
                              className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                passwords.newPassword &&
                                passwords.confirmPassword &&
                                passwords.newPassword !==
                                  passwords.confirmPassword
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                              placeholder="Confirm new password"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowPassword((prev) => ({
                                  ...prev,
                                  confirm: !prev.confirm,
                                }))
                              }
                              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700">
                              <Icon
                                name={showPassword.confirm ? "EyeOff" : "Eye"}
                                size={18}
                              />
                            </button>
                          </div>
                          {passwords.newPassword &&
                            passwords.confirmPassword &&
                            passwords.newPassword !==
                              passwords.confirmPassword && (
                              <p className="mt-1 text-xs text-red-600">
                                Passwords do not match
                              </p>
                            )}
                        </div>

                        {validation.passwordError && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <Icon
                                name="XCircle"
                                size={16}
                                className="text-red-600"
                              />
                              <p className="text-sm text-red-700">
                                {validation.passwordError}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-end">
                            <Button
                              onClick={handleChangePassword}
                              disabled={
                                loading ||
                                !passwords.currentPassword ||
                                !passwords.newPassword ||
                                !passwords.confirmPassword
                              }
                              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-2.5 text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                              {loading ? (
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Updating...
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Icon name="Key" size={14} />
                                  Update Password
                                </div>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions Tab */}
              {activeTab === "permissions" && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Icon
                          name="ShieldCheck"
                          size={20}
                          className="text-green-600"
                        />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Access Rights
                        </h2>
                        <p className="text-sm text-gray-600">
                          Your system permissions and access levels
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Icon
                            name="Shield"
                            size={24}
                            className="text-blue-600"
                          />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            Permission Overview
                          </h3>
                          <p className="text-xs text-gray-600">
                            Based on your role:{" "}
                            <span className="font-medium">{user?.role}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(user?.rights || {}).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className={`p-4 rounded-lg border transition-all duration-200 ${
                              value
                                ? "border-green-200 bg-green-50 hover:bg-green-100"
                                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                            }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 capitalize">
                                  {key.replace(/([A-Z])/g, " $1").trim()}
                                </h4>
                                <p className="text-xs text-gray-500 mt-1">
                                  {value
                                    ? "You have permission to access this feature"
                                    : "This feature is restricted for your role"}
                                </p>
                              </div>
                              <div
                                className={`p-2 rounded-full ${
                                  value ? "bg-green-100" : "bg-gray-100"
                                }`}>
                                <Icon
                                  name={value ? "CheckCircle" : "XCircle"}
                                  size={20}
                                  className={
                                    value ? "text-green-600" : "text-gray-400"
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <Icon name="Info" size={16} className="text-blue-500" />
                        <p>
                          For permission changes, please contact your system
                          administrator at{" "}
                          <span className="font-medium">admin@example.com</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
